import type { ResolvedRefArray, ResolvedVariantRelation, VariantSplit } from '@wix/document-services-types'
import { removePrefix } from '@wix/document-manager-utils'
import _ from 'lodash'

const REF_PREFIX = '#'
const VARIANT_RELATION_TYPE = 'VariantRelation'

export const stripHashIfExists = (query: string): string => removePrefix(query, REF_PREFIX)

export interface VariantProcessor<T> {
    onDefault(value: T): void
    onVariant(breakpointId: string, value: T): void
}

export const processVariantSplit = <T>(split: VariantSplit<T> | undefined, processor: VariantProcessor<T>): void => {
    if (!split) {
        return
    }

    if (split.default) {
        processor.onDefault(split.default)
    }

    for (const variant of split.variants ?? []) {
        const breakpointId = stripHashIfExists(variant.conditions[0])
        if (breakpointId && variant.value) {
            processor.onVariant(breakpointId, variant.value)
        }
    }
}

const isVariantRelation = <T>(value: T | ResolvedVariantRelation<T>): value is ResolvedVariantRelation<T> =>
    (value as ResolvedVariantRelation<T>).type === VARIANT_RELATION_TYPE

export const refArrayToVariantSplit = <T>(refArray: ResolvedRefArray<T> | undefined): VariantSplit<T> | undefined => {
    const values = refArray?.values
    if (!values?.length) {
        return undefined
    }

    const defaultValue = _.find(values, value => !isVariantRelation(value)) as T | undefined
    const variantRelations = _.filter(values, isVariantRelation)

    return {
        default: defaultValue,
        variants: _.map(variantRelations, relation => ({
            conditions: relation.variants,
            value: relation.to,
        })),
    }
}
